import { snippetCompletion, type Completion, type CompletionSource } from "@codemirror/autocomplete";
import { Filetype } from "idfweb-vfs";

import {
    BUILTIN_COMMANDS,
    buildSystem,
    enclosingCommand,
    indexCmake,
    propertyKind,
    sharedV1Files,
    type IdfCmake,
} from "./cmake-commands.ts";
import type { RuntimeLink } from "./runtime.ts";

const V1_DIR = "/idf/tools/cmake";
const V2_DIR = "/idf/tools/cmakev2";

/**
 * Completion in CMake files: command names outside an argument list, and the
 * keywords and property names of an ESP-IDF command inside one. The ESP-IDF
 * commands are those of the build system the project root uses, read from the
 * guest on first use; a failed or empty read is not remembered.
 */
export function cmakeCompletion(
    runtime: RuntimeLink,
    text: (path: string) => Promise<string | null>,
    projectRoot: () => string,
): CompletionSource {
    let idf: Promise<{ v1: IdfCmake; v2: IdfCmake }> | undefined;

    const loadIdf = async () => {
        const v1 = await readCmakeFiles(runtime, V1_DIR);
        if (v1.size === 0) throw new Error(`no CMake files in ${V1_DIR}`);
        // An ESP-IDF without cmakev2 builds every project with v1.
        const v2 = await readCmakeFiles(runtime, V2_DIR).catch(() => new Map<string, string>());
        if (v2.size === 0) return { v1: indexCmake([...v1.values()]), v2: indexCmake([...v1.values()]) };
        const shared = sharedV1Files([...v2.values()]).flatMap((name) => v1.get(name) ?? []);
        return { v1: indexCmake([...v1.values()]), v2: indexCmake([...v2.values(), ...shared]) };
    };

    return async (context) => {
        const word = context.matchBefore(/[A-Za-z_][A-Za-z0-9_]*/);
        if (word === null && !context.explicit) return null;
        const from = word?.from ?? context.pos;
        const line = context.state.doc.lineAt(context.pos);
        if (line.text.slice(0, context.pos - line.from).includes("#")) return null;

        idf ??= loadIdf().catch(() => {
            idf = undefined;
            return { v1: indexCmake([]), v2: indexCmake([]) };
        });
        const system = buildSystem((await text(`${projectRoot()}/CMakeLists.txt`)) ?? "") ?? "v1";
        const { commands: idfCommands, properties } = (await idf)[system];

        const inside = enclosingCommand(context.state.sliceDoc(0, from), from);
        if (inside !== undefined) {
            const keywords = idfCommands.get(inside.toLowerCase())?.keywords ?? [];
            const kind = propertyKind(inside);
            const options: Completion[] = [
                ...keywords.map((label) => ({ label, type: "keyword" })),
                ...[...(kind === undefined ? [] : properties[kind])]
                    .sort()
                    .map((label) => ({ label, type: "property" })),
            ];
            return options.length === 0 ? null : { from, options, validFor: /^[A-Za-z0-9_]*$/ };
        }

        const options = BUILTIN_COMMANDS.filter((name) => !idfCommands.has(name.toLowerCase())).map((name) =>
            snippetCompletion(`${name}(\${})`, { label: name, type: "function", detail: "CMake" }),
        );
        for (const command of idfCommands.values()) {
            options.push(
                snippetCompletion(`${command.name}(\${})`, {
                    label: command.name,
                    type: "function",
                    detail: `(${command.params})`,
                    info: command.doc === "" ? undefined : () => documentation(command.doc),
                }),
            );
        }
        return { from, options, validFor: /^[A-Za-z0-9_]*$/ };
    };
}

function documentation(doc: string): HTMLElement {
    const pre = document.createElement("pre");
    pre.className = "wb-completion-doc";
    pre.textContent = doc;
    return pre;
}

/** The `.cmake` files directly in `dir`, by name. */
async function readCmakeFiles(runtime: RuntimeLink, dir: string): Promise<Map<string, string>> {
    const texts = new Map<string, string>();
    for (const entry of await runtime.fs.readdir(dir)) {
        if (entry.filetype !== Filetype.REGULAR_FILE || !entry.name.endsWith(".cmake")) continue;
        texts.set(entry.name, new TextDecoder().decode(await runtime.fs.readFile(`${dir}/${entry.name}`)));
    }
    return texts;
}
